import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBoard } from "../board/BoardContext.jsx";
import { boardApi } from "../api/client.js";
import { isoWeek, pad2, ymd } from "../lib/domain.js";
import { Section } from "../components/drawer/Section.jsx";
import { HealthBox } from "../components/drawer/HealthBox.jsx";
import { FlowEditor } from "../components/drawer/FlowEditor.jsx";
import { TableEditor } from "../components/drawer/TableEditor.jsx";
import { KpiRows } from "../components/drawer/KpiRows.jsx";
import { PlayerRows } from "../components/drawer/PlayerRows.jsx";
import { TaskRows } from "../components/drawer/TaskRows.jsx";
import { useMailEnabled } from "../lib/useMailEnabled.js";
import { useToast } from "../components/Toast.jsx";

/* "1 KPI" rather than "1 KPIs". Only the regular plurals appear here, so
   an `s` is the whole rule. */
function count(n, noun) {
  return n + " " + noun + (n === 1 ? "" : "s");
}

/* One playground, in full, at its own URL (/playground/p03).
 *
 * Every field goes through the board's edit(), which shows the change at
 * once and queues it. Nothing reaches the server until Save is pressed.
 * The three actions the server owns — logging a review, overriding health,
 * stamping a KPI as reviewed — go straight there instead, and save the
 * queue on their way past. */
export function PlaygroundDrawer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { board, edit, act, loading, remove, save, discard, isDirty } = useBoard();
  const mailEnabled = useMailEnabled();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [savingNow, setSavingNow] = useState(false);
  const [deletingNow, setDeletingNow] = useState(false);

  const pg = board?.find((p) => p.id === id) || null;
  const unsaved = pg ? isDirty(pg.id) : false;

  /* Closing is the one moment an unsaved edit can be forgotten about, so it
     is the one moment to ask. Everything that leaves — Escape, the scrim,
     the Close button — comes through here, because a guard that only the
     button honours is not a guard. */
  const close = useCallback(() => {
    if (unsaved) setLeaving(true);
    else navigate("/");
  }, [unsaved, navigate]);

  /* Escape closes the active confirmation modal or unsaved prompt first, then drawer */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (confirming) setConfirming(false);
        else if (leaving) setLeaving(false);
        else close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, confirming, leaving]);

  if (loading) return null;

  if (!pg) {
    return (
      <>
        <div className="scrim open" onClick={close} />
        <aside className="drawer open" aria-label="Playground detail">
          <header className="dhead">
            <span className="idx">—</span>
            <h2 className="dtitle">Not found</h2>
            <button className="btn btn-sm" type="button" onClick={close}>
              Close
            </button>
          </header>
          <div className="dbody">
            <p className="empty">
              There is no playground <span className="mono">{id}</span> on this board.
            </p>
          </div>
        </aside>
      </>
    );
  }

  /* The only way an edit here reaches the server. It stays pressable with
     nothing queued — a Save button that greys out is a Save button nobody
     can find — and says so rather than doing nothing silently. */
  async function saveNow() {
    setSavingNow(true);
    try {
      /* On a failure the board has already said so; saying "Saved" over
         the top of that would be the one thing this button must not do. */
      if (await save(pg.id)) toast(unsaved ? "Saved" : "Already saved");
    } finally {
      setSavingNow(false);
    }
  }

  const field = (name) => (e) => edit(pg.id, (d) => { d[name] = e.target.value; return d; });
  const template = (next) => edit(pg.id, (d) => { d.template = next; return d; });

  return (
    <>
      <div className="scrim open" onClick={close} />
      <aside className="drawer open" aria-label={pg.name + " detail"}>
        <header className="dhead">
          <span className="idx">{pad2(pg.idx)}</span>
          <input
            className="in in-lg"
            value={pg.name}
            aria-label="Playground name"
            onChange={field("name")}
          />
          <button
            className={"btn btn-sm" + (unsaved ? " btn-solid" : "")}
            type="button"
            onClick={saveNow}
            disabled={savingNow}
            title={
              unsaved
                ? "Write this playground's edits now"
                : "Nothing to save — every edit here is written"
            }
          >
            {savingNow ? "Saving…" : "Save"}
          </button>
          {/* Anyone signed in can remove a playground — the server allows
              it too, and the confirmation below is what stands between the
              button and the deletion. */}
          <button
            className="btn btn-sm btn-armed"
            type="button"
            onClick={() => setConfirming(true)}
            disabled={confirming}
          >
            Remove
          </button>
          <button className="btn btn-sm" type="button" onClick={close}>
            Close
          </button>
        </header>

        <div className="dbody">
          <Section label="Identity">
            <div className="field">
              <label className="label" htmlFor="d-owner">
                Owner
              </label>
              <input
                className="in"
                id="d-owner"
                value={pg.owner}
                placeholder="Who runs this playground"
                onChange={field("owner")}
              />
            </div>
            <div className="field" style={{ marginTop: "10px" }}>
              <label className="label" htmlFor="d-description">
                Notes
              </label>
              <textarea
                className="in"
                id="d-description"
                value={pg.description}
                placeholder="Context, dependencies, anything the team should know"
                onChange={field("description")}
              />
            </div>
          </Section>

          <Section label="Health & cadence">
            <HealthBox
              pg={pg}
              mailEnabled={mailEnabled}
              onSetHealth={(v) =>
                act(pg.id, () => boardApi.setHealth(pg.id, v), v ? "Health set by hand" : "Back on the clock")
              }
              onLogMeeting={() =>
                act(pg.id, () => boardApi.logMeeting(pg.id), "Meeting logged today")
              }
              onCadence={(v) => edit(pg.id, (d) => { d.cadence = v; return d; })}
              onNotifyReview={() =>
                navigate("/note?kind=review&playground=" + pg.id)
              }
            />
          </Section>

          <Section
            label="Process flow"
            actions={
              <button
                className="btn btn-sm"
                type="button"
                onClick={() =>
                  template({
                    ...pg.template,
                    flow: [...pg.template.flow, { title: "", description: "" }],
                  })
                }
              >
                + Stage
              </button>
            }
          >
            <FlowEditor
              flow={pg.template.flow}
              onChange={(flow) => template({ ...pg.template, flow })}
            />
          </Section>

          <Section
            label="Reference table"
            actions={
              <>
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() =>
                    template({
                      ...pg.template,
                      table: {
                        ...pg.template.table,
                        rows: [
                          ...pg.template.table.rows,
                          pg.template.table.columns.map(() => ""),
                        ],
                      },
                    })
                  }
                >
                  + Row
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() =>
                    template({
                      ...pg.template,
                      table: {
                        columns: [...pg.template.table.columns, ""],
                        rows: pg.template.table.rows.map((r) => [...r, ""]),
                      },
                    })
                  }
                >
                  + Column
                </button>
              </>
            }
          >
            <TableEditor
              table={pg.template.table}
              onChange={(table) => template({ ...pg.template, table })}
            />
          </Section>

          <Section
            label="KPIs"
            actions={
              <button
                className="btn btn-sm"
                type="button"
                onClick={() =>
                  edit(pg.id, (d) => {
                    d.kpis.push({
                      name: "",
                      value: "",
                      target: "",
                      unit: "",
                      reviewOn: "",
                      notifiedAt: "",
                      lastReviewedAt: "",
                    });
                    return d;
                  })
                }
              >
                + KPI
              </button>
            }
          >
            <KpiRows
              kpis={pg.kpis}
              mailEnabled={mailEnabled}
              onChange={(kpis) => edit(pg.id, (d) => { d.kpis = kpis; return d; })}
              onReviewed={(i) =>
                act(pg.id, () => boardApi.kpiReviewed(pg.id, i), "Marked reviewed")
              }
              onNotify={(i) =>
                navigate("/note?kind=kpi&playground=" + pg.id + "&index=" + i)
              }
            />
          </Section>

          <Section
            label="Players"
            actions={
              <button
                className="btn btn-sm"
                type="button"
                onClick={() =>
                  edit(pg.id, (d) => {
                    d.players.push({ name: "", role: "", email: "", status: "active" });
                    return d;
                  })
                }
              >
                + Player
              </button>
            }
          >
            <PlayerRows
              players={pg.players}
              onChange={(players) => edit(pg.id, (d) => { d.players = players; return d; })}
            />
          </Section>

          <Section
            label={"Tasks · week " + isoWeek(new Date())}
            actions={
              <>
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() =>
                    edit(pg.id, (d) => {
                      d.tasks.push({
                        title: "",
                        assignee: "",
                        status: "todo",
                        week: isoWeek(new Date()),
                        due: ymd(new Date()),
                        critical: false,
                        notifiedAt: "",
                      });
                      return d;
                    })
                  }
                >
                  + Task
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() =>
                    edit(pg.id, (d) => {
                      d.tasks = d.tasks.filter((t) => t.status !== "done");
                      return d;
                    })
                  }
                >
                  Clear completed
                </button>
              </>
            }
          >
            <TaskRows
              tasks={pg.tasks}
              mailEnabled={mailEnabled}
              onChange={(tasks) => edit(pg.id, (d) => { d.tasks = tasks; return d; })}
              onNotify={(i) =>
                navigate("/note?kind=task&playground=" + pg.id + "&index=" + i)
              }
            />
          </Section>
        </div>
      </aside>

      {/* Notification before closing board without save (bottom-left, Image 1 design) */}
      {leaving && (
        <aside
          className="confirmbar toast-bottom-prompt show bad"
          role="alert"
          aria-live="assertive"
        >
          <div className="toast-accent-line" />
          <div className="toast-prompt-body">
            <div className="toast-icon-wrap" aria-hidden="true">
              <span className="toast-icon-circle bad">!</span>
            </div>
            <div className="toast-prompt-content">
              <div className="toast-prompt-header">
                <div className="toast-prompt-title">Unsaved Changes</div>
                <button
                  type="button"
                  className="toast-close"
                  aria-label="Keep editing"
                  onClick={() => setLeaving(false)}
                >
                  ×
                </button>
              </div>
              <div className="toast-prompt-msg msg">
                <b>This playground has unsaved changes.</b> Nothing here has been written yet — closing without saving gives the edits up and puts the board back the way it was.
              </div>
              <div className="toast-prompt-actions">
                <button
                  className="btn btn-sm btn-solid toast-action-save"
                  type="button"
                  disabled={savingNow}
                  onClick={async () => {
                    setSavingNow(true);
                    try {
                      if (await save(pg.id)) navigate("/");
                      else setLeaving(false);
                    } finally {
                      setSavingNow(false);
                    }
                  }}
                >
                  {savingNow ? "Saving…" : "Save and close"}
                </button>
                <button
                  className="btn btn-sm btn-armed toast-action-discard"
                  type="button"
                  disabled={savingNow}
                  onClick={async () => {
                    await discard(pg.id);
                    toast("Changes discarded");
                    navigate("/");
                  }}
                >
                  Discard them
                </button>
                <button
                  className="btn btn-sm toast-action-cancel"
                  type="button"
                  disabled={savingNow}
                  onClick={() => setLeaving(false)}
                >
                  Keep editing
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Confirmation modal before deleting board (Image 2 design) */}
      {confirming && (
        <>
          <div
            className="modal-scrim"
            style={{ zIndex: 65 }}
            onClick={() => !deletingNow && setConfirming(false)}
          />
          <div
            className="modal delete-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-board-title"
          >
            <div className="delete-modal-inner">
              <div className="delete-modal-header">
                <h3 id="delete-board-title" className="delete-modal-title">Delete Board</h3>
                <p className="delete-modal-subtitle">
                  Please confirm that you want to permanently delete this board.
                </p>
              </div>

              <div className="delete-modal-card">
                <div className="delete-modal-target-title">
                  {pg.name || "Untitled playground"}
                </div>
                <div className="delete-modal-target-stats">
                  This board and everything on it — {count(pg.kpis.length, "KPI")}, {count(pg.players.length, "player")}, and {count(pg.tasks.length, "task")} — will be deleted.
                </div>
                <div className="delete-modal-target-warn">
                  There is no undo, so export the board from Data first if you may want it back.
                </div>
              </div>

              <div className="delete-modal-footer">
                <button
                  className="btn btn-modal-cancel"
                  type="button"
                  disabled={deletingNow}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-modal-delete"
                  type="button"
                  disabled={deletingNow}
                  onClick={async () => {
                    setDeletingNow(true);
                    try {
                      const boardName = pg.name || "Playground";
                      await remove(pg.id);
                      toast(boardName + " deleted");
                      navigate("/");
                    } catch {
                      setConfirming(false);
                    } finally {
                      setDeletingNow(false);
                    }
                  }}
                >
                  {deletingNow ? "Deleting…" : "Delete it"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
